import Link from "next/link";
import Iconify from "@/components/ui/Iconify";

/**
 * EmptyState — shared, minimal ("A") empty state (docs/DESIGN-SYSTEM.md §08).
 *
 * Quiet by design: a muted line icon, a one-line title, an optional sub line,
 * and (only when the user can act) a single quiet action. No gradient/filled
 * buttons — an empty state is never a "primary" surface.
 *
 * @param {string|React.ReactNode} icon  Iconify name (e.g. "lucide:bookmark")
 *   or a ReactNode (an inline line SVG). Falls back to a generic line icon.
 * @param {string} title        Required one-line message (bold).
 * @param {string} [description] Optional muted sub line.
 * @param {{ label: string, onClick?: Function, href?: string,
 *   variant?: "ghost"|"outline" }} [action] Optional single action.
 */
const EmptyState = ({ icon, title, description, action }) => {
  const iconClass =
    "!w-10 !h-10 !cursor-default !text-black/35 dark:!text-white/30";

  let iconNode;
  if (!icon) {
    iconNode = <Iconify icon="lucide:inbox" className={iconClass} />;
  } else if (typeof icon === "string") {
    iconNode = <Iconify icon={icon} className={iconClass} />;
  } else {
    iconNode = icon;
  }

  let actionNode = null;
  if (action?.label && (action.onClick || action.href)) {
    const variant = action.variant || "ghost";
    const base =
      "mt-1 inline-flex items-center justify-center text-sm font-medium transition";
    const styles =
      variant === "outline"
        ? "rounded-[8px] border border-black/15 dark:border-white/20 px-4 py-2 text-black/70 dark:text-white/70 hover:bg-black/[0.03] dark:hover:bg-white/[0.06]"
        : "text-red hover:opacity-80";
    const cls = `${base} ${styles}`;

    if (action.href) {
      actionNode = (
        <Link href={action.href} onClick={action.onClick} className={cls}>
          {action.label}
        </Link>
      );
    } else {
      actionNode = (
        <button type="button" onClick={action.onClick} className={cls}>
          {action.label}
        </button>
      );
    }
  }

  return (
    <div className="w-full flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {iconNode}
      <span className="max-w-[230px] text-[14px] font-bold leading-snug text-black/80 dark:text-white/80">
        {title}
      </span>
      {description && (
        <span className="max-w-[230px] text-[12.5px] leading-snug text-black/45 dark:text-white/40">
          {description}
        </span>
      )}
      {actionNode}
    </div>
  );
};

export default EmptyState;
